import {
  recipeConfigSource,
  recipeSchedulingKnobs,
  SESSION_DISPLAY_NAME_MAX_LENGTH,
  type TrainingSessionDetailView,
} from "@/api";
import { learningCoreRevision } from "../sessionDetail/trainingSessionDetailFormat";
import { derivedSessionDefaultName } from "../trainingSessionFormat";
import type {
  KeyValuePair,
  SubmitDatasetSelection,
  SubmitInitSourceSession,
} from "./submitWizardForm";

// 세션의 제출 스냅샷을 제출 위저드 프리필로 옮긴다. 진입점은 둘이고 매퍼는 하나다.
//   - 실패(조치 필요) 세션의 "설정 고쳐 다시 제출"(ZERG-257).
//   - "재학습"(ZERG-266 축2).
// 승계할 수 없는 항목은 막지 않고 건너뛰되 omissions 문구로 남긴다. 사용자는 위저드에서
// 수동으로 보충한 뒤 제출한다.
//
// 재제출은 원본과의 계보가 남지 않으므로 기본 이름 파생(-x2)이 유일한 연결 고리다. 재학습은
// 소스 세션 id 를 함께 보내므로 상류가 계보를 남긴다.

export type SubmitFormPrefill = {
  datasets: SubmitDatasetSelection[];
  targetDatasetIndex: number | null;
  branchRef: string | null;
  // 원본이 실제로 실행한 커밋(full SHA). 있으면 위저드가 그 커밋으로 고정해 열어서 같은 코드로 재현한다.
  // 사용자는 Commit 드롭다운에서 "브랜치 최신"으로 고정을 풀 수 있다. null 은 빌더가 아직 해석하지
  // 않았거나 구서버 응답이라 커밋을 모르는 상태이고, 그때는 브랜치 최신을 따른다.
  commitSha: string | null;
  // recipe 의 config 이름은 제출할 때 정규화된 형태라 브랜치 목록의 경로와 다를 수 있다.
  // 경로 복원(findConfigPath)은 목록을 가진 컨트롤러가 한다.
  configName: string | null;
  // null = 원본에 override 기록이 없음. 컨트롤러가 팀 관례 시드를 그대로 쓴다.
  // [] = 원본이 명시적으로 0건. 시드를 억제하고 빈 상태로 연다(원본에 없던 override 를
  // 다시 만들지 않는다). wire 의 부재/`{}` 구분을 그대로 옮긴 값이다.
  overrideRows: KeyValuePair[] | null;
  nodes: number | null;
  gpusPerNode: number | null;
  minLifetimeSeconds: number | null;
  // 원본이 미지정(null)이면 폼 기본값을 유지한다. 3-state 를 boolean 으로 합치지 않는다.
  allowSpot: boolean | null;
  displayName: string;
  // 재학습으로 열 때의 시작점 소스 세션. null 이면 위저드가 scratch 로 열린다(재제출 진입점).
  retrainSource: SubmitInitSourceSession | null;
  // 승계하지 못한 항목의 안내 문구. 위저드를 열 때 호출부가 토스트로 표시한다.
  omissions: string[];
};

export type SessionPrefillOptions = {
  // 있으면 재학습 프리필이다. 시작점이 이 세션의 최신 체크포인트가 되고 자원이 1×1 로 잠긴다.
  retrainSource?: SubmitInitSourceSession | null;
};

// override 값을 폼 행 문자열로 되돌린다. 폼 행은 parseListOrScalar 로 타입을 다시 읽으므로
// 문자열은 그대로 두고, 나머지(숫자·불리언·null·리스트)는 왕복 가능한 JSON 표기로 둔다.
function overrideValueText(value: unknown): string {
  return typeof value === "string" ? value : JSON.stringify(value);
}

export function buildSessionResubmitPrefill(
  detail: TrainingSessionDetailView,
  parentTitle: string,
  options: SessionPrefillOptions = {},
): SubmitFormPrefill {
  const omissions: string[] = [];
  const retrainSource = options.retrainSource ?? null;
  // finetune 계열은 상류 fine-tune 진입이 단일 프로세스라 BE 가 1노드×1GPU 만 받는다(그 외 400).
  // 원본의 자원과 관계없이 덮어쓰되, 값이 실제로 바뀔 때만 안내한다. 바뀌지 않으면 안내할 내용이 없다.
  //
  // 스냅샷에 자원이 없으면 "1이었다"가 아니라 **모르는 것**이다. 폼 기본값은 BE 가 제출 옵션으로
  // 주는 `resource_defaults` 가 소유한다(현재 값은 1×1. registry 기본값 상수). 이 값을 wire 로 받는
  // 이유는 "submit 요청 기본값과 같은 출처를 써서 drift 를 막기" 위해서다. FE 가 여기서 1 을 따로
  // 가지면 그 목적이 깨지고, BE 가 기본값을 바꾸는 순간 알림 없이 어긋난다.
  // 값을 비교할 수도 없다. 이 함수는 순수 매퍼이고, 두 호출부 모두 옵션을 받기 전에 위저드를
  // 연다. 그래서 값이 바뀌었다고 단정하지 않고 기록이 없다는 사실만 안내한다.
  if (retrainSource !== null) {
    const resources = detail.recipe?.resources;
    const axes = [resources?.nodes ?? null, resources?.gpus_per_node ?? null];
    if (axes.some((axis) => axis !== null && axis !== 1)) {
      omissions.push("재학습은 1노드×1GPU 로 고정되어 원본의 자원 설정은 승계되지 않습니다.");
    } else if (axes.some((axis) => axis === null)) {
      omissions.push(
        "원본의 자원 설정이 제출 스냅샷에 없습니다. 재학습은 1노드×1GPU 로 고정됩니다.",
      );
    }
  }

  // ── 학습 입력. registry 원본이 남아 있는 train/val 만 폼이 다룰 수 있다 ──
  //
  // 카드의 이름·섬네일은 `registry_ref` 에서 옮긴다. training 쪽 이름(`reg-ds-<uuid>`)은
  // 사용자에게 뜻이 없어서 화면에 표시하지 않는다(submittedDatasetLabel 과 같은 규칙). uuid 는
  // 있지만 참조가 없으면 원본이 삭제된 것이라 다시 학습에 쓸 수 없다. 담지 않고 안내한다.
  const datasets: SubmitDatasetSelection[] = [];
  let targetDatasetIndex: number | null = null;
  let unmappedCount = 0;
  let deletedCount = 0;
  let unsupportedRoleCount = 0;
  let targetExcluded = false;
  for (const entry of detail.datasets) {
    if (entry.role !== "train" && entry.role !== "val") {
      unsupportedRoleCount += 1;
      continue;
    }
    const ref = entry.registry_ref ?? null;
    if (entry.registry_dataset_uuid === null || ref === null) {
      if (entry.registry_dataset_uuid === null) {
        unmappedCount += 1;
      } else {
        deletedCount += 1;
      }
      if (entry.is_target === true) {
        targetExcluded = true;
      }
      continue;
    }
    if (entry.is_target === true) {
      targetDatasetIndex = datasets.length;
    }
    datasets.push({
      uuid: entry.registry_dataset_uuid,
      label: ref.display_name,
      // 상세 응답에는 요약 값(팔 구성 · 카메라 · 에피소드)이 없어서 섬네일만 옮긴다. 카드는
      // meta 가 비면 그 줄을 그리지 않는다.
      preview: { thumbnailUrl: ref.thumbnail_url ?? null, meta: [] },
      role: entry.role,
      weight: entry.weight ?? null,
      // 세트는 승계한다. 그 세트를 지금도 쓸 수 있는지는 위저드가 후보를 받은 뒤 판정하고,
      // 없으면 비우고 안내한다. 프리필 시점에는 후보 목록이 없다.
      annotationSetUuid: entry.annotation_set_uuid ?? null,
    });
  }
  if (unmappedCount > 0) {
    omissions.push(`registry 에 연결되지 않은 Training 입력 ${unmappedCount}건은 제외했습니다.`);
  }
  if (deletedCount > 0) {
    omissions.push(`삭제된 Dataset ${deletedCount}건은 제외했습니다.`);
  }
  if (unsupportedRoleCount > 0) {
    omissions.push(`Training·검증이 아닌 역할의 입력 ${unsupportedRoleCount}건은 제외했습니다.`);
  }
  if (targetExcluded) {
    omissions.push("Training 타깃 Dataset이 제외되어 타깃 지정이 비었습니다.");
  }

  // ── 제출 스냅샷. 저널이 없는 세션은 recipe 가 없다 ──
  const recipe = detail.recipe ?? null;
  if (recipe === null) {
    omissions.push("제출 스냅샷이 없어 config·자원 설정은 기본값으로 열립니다.");
    return {
      datasets,
      targetDatasetIndex,
      branchRef: null,
      commitSha: null,
      configName: null,
      overrideRows: null,
      nodes: retrainSource === null ? null : 1,
      gpusPerNode: retrainSource === null ? null : 1,
      minLifetimeSeconds: null,
      allowSpot: null,
      displayName: derivedSessionDefaultName(parentTitle, SESSION_DISPLAY_NAME_MAX_LENGTH),
      retrainSource,
      omissions,
    };
  }

  // config 전체 본문(inline_yaml)은 recipe 에 오지 않는다. 전체 본문을 승계할 수 없다는 사실은
  // 안내로 남긴다. 이름·override 는 소스와 관계없이 있으면 승계한다.
  //
  // 출처가 없다는 것은 "inline 아님"이 아니다(이름으로 추론할 수 없다). 편집본이었다면 안내 없이
  // 편집이 사라지므로, 모를 때는 불확실하다고 표시한다. 표시하지 않아서 편집을 잃는 비용이
  // 불필요한 안내를 표시하는 비용보다 크다.
  const configSource = recipeConfigSource(recipe.config);
  if (configSource === "inline_yaml") {
    omissions.push(
      "직접 편집한 config 본문은 승계되지 않습니다. 원본 Session의 Config 탭에서 복사해 주세요.",
    );
  } else if (configSource === null) {
    omissions.push(
      "원본의 config 출처를 알 수 없습니다. 직접 편집한 본문이었다면 승계되지 않으니 원본 Session의 Config 탭에서 확인해 주세요.",
    );
  }
  // 부재/null(미기록)과 `{}`(명시적 0건)는 다른 상태다. `?? {}` 로 합치면 override 를 모두
  // 지우고 제출한 세션을 재제출할 때 팀 관례 시드가 다시 생긴다.
  const recordedOverrides = recipe.config.overrides ?? null;
  const overrideRows: KeyValuePair[] | null =
    recordedOverrides === null
      ? null
      : Object.entries(recordedOverrides).map(([key, value]) => ({
          key,
          value: overrideValueText(value),
        }));

  // learning core. 폼은 브랜치·커밋 두 축을 연다. wheel 제출은 브랜치를 새로 고르도록 안내한다.
  // 제출 ref 가 40-hex 였던 세션은 브랜치를 모르므로 브랜치를 비우고 커밋만 고정한다(판정은 세션
  // 상세 복사 문구와 같은 learningCoreRevision 이 소유한다).
  let branchRef: string | null = null;
  let commitSha: string | null = null;
  const learningCore = recipe.learning_core ?? null;
  if (learningCore !== null) {
    if (learningCore.kind === "branch") {
      const revision = learningCoreRevision(learningCore);
      branchRef = revision.branch;
      commitSha = revision.sha;
    } else {
      omissions.push("wheel 기반 learning core 는 승계되지 않습니다. 브랜치를 골라 주세요.");
    }
  }

  const resources = recipe.resources ?? null;
  const knobs = recipeSchedulingKnobs(recipe);
  return {
    datasets,
    targetDatasetIndex,
    branchRef,
    commitSha,
    configName: recipe.config.name ?? null,
    overrideRows,
    nodes: retrainSource === null ? (resources?.nodes ?? null) : 1,
    gpusPerNode: retrainSource === null ? (resources?.gpus_per_node ?? null) : 1,
    minLifetimeSeconds: knobs.minLifetimeSeconds,
    allowSpot: knobs.allowSpot,
    displayName: derivedSessionDefaultName(parentTitle, SESSION_DISPLAY_NAME_MAX_LENGTH),
    retrainSource,
    omissions,
  };
}
